<template>
  <div class="dice-admin-config">
    <!-- Header -->
    <div class="config-header">
      <h2 class="config-title">{{ $t('admin.config.dice.title') }}</h2>
      <p class="config-subtitle">{{ $t('admin.config.dice.subtitle') }}</p>
    </div>

    <div v-if="loading" class="loading-state">{{ $t('admin.config.loading') }}</div>
    <div v-else-if="loadError" class="error-banner">{{ loadError }}</div>

    <form v-else class="settings-form" @submit.prevent="saveConfig">

      <!-- House Edge -->
      <section class="settings-section">
        <h3 class="section-title">{{ $t('admin.config.section_house_edge') }}</h3>
        <div class="form-row">
          <label class="form-label">
            {{ $t('admin.config.label_house_edge') }}
            <input
              v-model.number="form.house_edge_pct"
              type="number"
              step="0.5"
              min="1"
              max="10"
              class="form-input"
              required
            />
            <span class="form-hint">{{ $t('admin.config.hint_house_edge') }}</span>
          </label>
          <div class="rtp-display">
            <span class="rtp-label">{{ $t('admin.config.label_rtp') }}</span>
            <span class="rtp-value">{{ rtpDisplay }}%</span>
          </div>
        </div>
      </section>

      <!-- Bet Limits -->
      <section class="settings-section">
        <h3 class="section-title">{{ $t('admin.config.section_bet_limits') }}</h3>
        <div class="form-row">
          <label class="form-label">
            {{ $t('admin.config.label_min_bet') }}
            <input
              v-model="form.min_bet"
              type="number"
              step="0.01"
              min="0.01"
              class="form-input"
              required
            />
          </label>
          <label class="form-label">
            {{ $t('admin.config.label_max_bet') }}
            <input
              v-model="form.max_bet"
              type="number"
              step="0.01"
              min="0.01"
              class="form-input"
              required
            />
          </label>
        </div>
        <div class="form-row">
          <label class="form-label">
            {{ $t('admin.config.label_max_payout') }}
            <input
              v-model="form.max_payout"
              type="number"
              step="0.01"
              min="0.01"
              class="form-input"
              required
            />
            <span class="form-hint">{{ $t('admin.config.hint_max_bet_gte_min') }}</span>
          </label>
        </div>
      </section>

      <div v-if="saveError" class="error-banner">{{ saveError }}</div>
      <div v-if="saveSuccess" class="success-banner">{{ saveSuccess }}</div>

      <div class="form-actions">
        <button type="submit" class="btn btn-primary" :disabled="saving">
          {{ saving ? $t('admin.config.saving') : $t('admin.config.save_config') }}
        </button>
        <button type="button" class="btn btn-secondary" :disabled="saving" @click="resetToDefaults">
          {{ $t('admin.config.reset_to_defaults') }}
        </button>
        <span v-if="sourceLabel" class="source-badge" :class="sourceClass">
          {{ sourceLabel }}
        </span>
      </div>
    </form>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import apiClient from '@/api/client'
import { useToastStore } from '@/stores/toast'

interface DiceConfigResponse {
  source: 'database' | 'default'
  house_edge_pct: number
  min_bet: string
  max_bet: string
  max_payout: string
  rtp: number
}

const { t } = useI18n()
const toast = useToastStore()

const loading = ref(true)
const saving = ref(false)
const loadError = ref('')
const saveError = ref('')
const saveSuccess = ref('')

const source = ref<'database' | 'default'>('default')

const DEFAULTS = {
  house_edge_pct: 1.0,
  min_bet: '1.00',
  max_bet: '10000.00',
  max_payout: '10000.00',
}

const form = reactive({
  house_edge_pct: DEFAULTS.house_edge_pct,
  min_bet: DEFAULTS.min_bet,
  max_bet: DEFAULTS.max_bet,
  max_payout: DEFAULTS.max_payout,
})

const rtpDisplay = computed(() => (100 - form.house_edge_pct).toFixed(2))

const sourceLabel = computed(() => (source.value === 'database' ? t('admin.config.saved_to_db') : t('admin.config.using_defaults')))
const sourceClass = computed(() => (source.value === 'database' ? 'source-db' : 'source-default'))

async function loadConfig() {
  loading.value = true
  loadError.value = ''
  try {
    const res = await apiClient.get<DiceConfigResponse>('/admin/api/games/dice/config')
    populateForm(res.data)
  } catch {
    loadError.value = t('admin.config.dice.error_load')
  } finally {
    loading.value = false
  }
}

function populateForm(data: DiceConfigResponse) {
  source.value = data.source
  form.house_edge_pct = data.house_edge_pct
  form.min_bet = data.min_bet
  form.max_bet = data.max_bet
  form.max_payout = data.max_payout
}

function resetToDefaults() {
  form.house_edge_pct = DEFAULTS.house_edge_pct
  form.min_bet = DEFAULTS.min_bet
  form.max_bet = DEFAULTS.max_bet
  form.max_payout = DEFAULTS.max_payout
  saveError.value = ''
  saveSuccess.value = ''
}

async function saveConfig() {
  saveError.value = ''
  saveSuccess.value = ''
  saving.value = true

  try {
    const res = await apiClient.post<DiceConfigResponse>('/admin/api/games/dice/config', {
      house_edge_pct: form.house_edge_pct,
      min_bet: form.min_bet,
      max_bet: form.max_bet,
      max_payout: form.max_payout,
    })
    populateForm(res.data)
    saveSuccess.value = t('admin.config.dice.save_success')
    toast.success(t('admin.config.dice.save_success'))
  } catch (err: unknown) {
    const e = err as { response?: { data?: { message?: string } } }
    saveError.value = e.response?.data?.message ?? t('admin.config.dice.error_save')
    toast.error(saveError.value)
  } finally {
    saving.value = false
  }
}

onMounted(loadConfig)
</script>

<style scoped>
.dice-admin-config {
  padding: 0;
}

.config-header {
  margin-bottom: 24px;
}

.config-title {
  font-size: 1.375rem;
  font-weight: 700;
  color: var(--text-primary, #fff);
  margin: 0 0 6px;
}

.config-subtitle {
  color: var(--text-secondary, #b1bad3);
  font-size: 0.875rem;
  margin: 0;
}

/* Loading / error / success */
.loading-state {
  color: var(--text-secondary, #b1bad3);
  padding: 32px;
  text-align: center;
}

.error-banner {
  background: rgba(239, 68, 68, 0.12);
  border: 1px solid rgba(239, 68, 68, 0.4);
  border-radius: 8px;
  color: var(--color-danger-light, #f87171);
  padding: 12px 16px;
  font-size: 0.875rem;
  margin-bottom: 16px;
}

.success-banner {
  background: rgba(26, 158, 110, 0.12);
  border: 1px solid rgba(26, 158, 110, 0.4);
  border-radius: 8px;
  color: var(--color-success-light, #34d399);
  padding: 12px 16px;
  font-size: 0.875rem;
  margin-bottom: 16px;
}

/* Form */
.settings-form {
  max-width: 680px;
}

.settings-section {
  background: var(--bg-secondary, #213743);
  border: 1px solid var(--border, #2d4a5a);
  border-radius: 10px;
  padding: 20px;
  margin-bottom: 20px;
}

.section-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--text-primary, #fff);
  margin: 0 0 16px;
}

.form-row {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 14px;
  align-items: flex-start;
}

.form-row:last-child {
  margin-bottom: 0;
}

.form-label {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 0.8rem;
  color: var(--text-secondary, #b1bad3);
  flex: 1;
  min-width: 180px;
}

.form-input {
  padding: 8px 12px;
  background: var(--bg-primary, #0f212e);
  border: 1px solid var(--border, #2d4a5a);
  border-radius: 6px;
  color: var(--text-primary, #fff);
  font-size: 0.875rem;
}

.form-hint {
  font-size: 0.72rem;
  color: var(--text-secondary, #b1bad3);
  opacity: 0.7;
}

/* RTP display inline with house edge */
.rtp-display {
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: center;
  justify-content: center;
  min-width: 80px;
  padding-top: 18px;
}

.rtp-label {
  font-size: 0.75rem;
  color: var(--text-secondary, #b1bad3);
}

.rtp-value {
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--color-success-light, #34d399);
}

/* Actions row */
.form-actions {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-top: 8px;
}

/* Source badge */
.source-badge {
  font-size: 0.75rem;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 6px;
}

.source-db {
  background: rgba(59, 130, 246, 0.2);
  color: var(--color-info-light, #60a5fa);
}

.source-default {
  background: rgba(107, 114, 128, 0.2);
  color: var(--color-muted, #9ca3af);
}

/* Buttons */
.btn {
  padding: 9px 20px;
  border-radius: 7px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: opacity 0.15s;
}

.btn:disabled {
  opacity: 0.55;
  cursor: default;
}

.btn-primary {
  background: var(--accent, #1a9e6e);
  color: var(--color-on-dark, #fff);
}

.btn-secondary {
  background: var(--bg-primary, #0f212e);
  color: var(--text-secondary, #b1bad3);
  border: 1px solid var(--border, #2d4a5a);
}
</style>
